import { dashFetch } from "@/api/client";
import { getDashSocket, connectDashSocket } from "@/socket/dashSocket";
import { DASH_ASSISTANT_EVENTS } from "@/socket/events";
import type { AssistantMessage, AssistantThinkingStep } from "@/types/account";

export type AssistantChatDonePayload = {
  requestId: string;
  site?: string;
  ok?: boolean;
  reply?: string;
  error?: string;
  actions?: unknown[];
  pendingMutations?: unknown[];
  thinkingSteps?: AssistantThinkingStep[];
};

export type AssistantChatHandlers = {
  onProgress?: (step: AssistantThinkingStep) => void;
  onDelta?: (text: string) => void;
  onDone?: (payload: AssistantChatDonePayload) => void;
  onError?: (error: string) => void;
};

function normalizeHistoryMessages(list: unknown[]): AssistantMessage[] {
  if (!Array.isArray(list)) return [];
  return list
    .filter((m) => m && typeof m === "object")
    .map((m) => {
      const row = m as AssistantMessage;
      return {
        id: row.id || `msg_${Math.random().toString(36).slice(2, 10)}`,
        role: (row.role === "assistant" ? "assistant" : "user") as "user" | "assistant",
        content: String(row.content || ""),
        thinkingSteps: Array.isArray(row.thinkingSteps) ? row.thinkingSteps : undefined,
        tables: row.tables,
        contentSearches: row.contentSearches,
        pendingMutations: row.pendingMutations
      };
    })
    .filter((m) => m.content && m.content !== "…");
}

export async function fetchAssistantHistory(domain: string): Promise<AssistantMessage[]> {
  const site = String(domain || "").trim();
  if (!site) return [];

  const sock = getDashSocket()?.connected ? getDashSocket() : connectDashSocket(site);
  if (sock?.connected) {
    const out = await new Promise<{ ok?: boolean; messages?: unknown[] }>((resolve) => {
      let done = false;
      const timer = window.setTimeout(() => {
        if (done) return;
        done = true;
        resolve({ ok: false });
      }, 12_000);
      sock.emit(DASH_ASSISTANT_EVENTS.history, { site, domain: site }, (resp: { ok?: boolean; messages?: unknown[] }) => {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        resolve(resp || { ok: false });
      });
    });
    if (out.ok && Array.isArray(out.messages)) {
      return normalizeHistoryMessages(out.messages);
    }
  }

  const data = await dashFetch<{ ok?: boolean; messages?: unknown[] }>(
    `/dashboard/api/assistant/history?domain=${encodeURIComponent(site)}`,
    { cache: "no-store", site }
  );
  if (data?.ok && Array.isArray(data.messages)) {
    return normalizeHistoryMessages(data.messages);
  }
  return [];
}

export function sendAssistantChat(args: {
  domain: string;
  requestId: string;
  messages: AssistantMessage[];
  context: Record<string, unknown>;
  handlers: AssistantChatHandlers;
}): () => void {
  const site = String(args.domain || "").trim();
  const requestId = String(args.requestId || "").trim();
  const sock = connectDashSocket(site);
  if (!sock) {
    args.handlers.onError?.("socket_unavailable");
    return () => {};
  }

  const onProgress = (payload: { requestId?: string; step?: AssistantThinkingStep }) => {
    if (payload?.requestId !== requestId) return;
    if (payload?.step) args.handlers.onProgress?.(payload.step);
  };
  const onDelta = (payload: { requestId?: string; text?: string }) => {
    if (payload?.requestId !== requestId) return;
    if (payload?.text) args.handlers.onDelta?.(payload.text);
  };
  const onDone = (payload: AssistantChatDonePayload) => {
    if (payload?.requestId !== requestId) return;
    args.handlers.onDone?.(payload);
    cleanup();
  };
  const onError = (payload: { requestId?: string; error?: string }) => {
    if (payload?.requestId !== requestId) return;
    args.handlers.onError?.(String(payload?.error || "assistant_failed"));
    cleanup();
  };

  const cleanup = () => {
    sock.off(DASH_ASSISTANT_EVENTS.progress, onProgress);
    sock.off(DASH_ASSISTANT_EVENTS.delta, onDelta);
    sock.off(DASH_ASSISTANT_EVENTS.done, onDone);
    sock.off(DASH_ASSISTANT_EVENTS.error, onError);
  };

  sock.on(DASH_ASSISTANT_EVENTS.progress, onProgress);
  sock.on(DASH_ASSISTANT_EVENTS.delta, onDelta);
  sock.on(DASH_ASSISTANT_EVENTS.done, onDone);
  sock.on(DASH_ASSISTANT_EVENTS.error, onError);

  const emitChat = () => {
    sock.emit(
      DASH_ASSISTANT_EVENTS.chat,
      {
        requestId,
        site,
        domain: site,
        messages: args.messages,
        context: args.context
      },
      (ack: { ok?: boolean; error?: string }) => {
        if (!ack?.ok) {
          args.handlers.onError?.(String(ack?.error || "chat_rejected"));
          cleanup();
        }
      }
    );
  };

  if (sock.connected) {
    emitChat();
  } else {
    sock.once("connect", emitChat);
  }

  return cleanup;
}
